import z from "zod";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getRemoteCollectionState, type RemoteCollectionState } from "src/api/remoteCollectionState";
import { zodParseWithLog } from "../../apiErrorLogs";
import { fetchAllHydraMembers } from "./fetchAllHydraMembers";
import { assertTransportDetailScope, isTransportDetailNotFoundError } from "./transportDetailScope";
import { TransportMutationContractError } from "./TransportMutationContractError";

/**
 * GeoJSON LineString as returned by sure-ai for a computed transport route.
 * Coordinates are `[longitude, latitude]` pairs (GeoJSON convention).
 */
export const RouteGeometrySchema = z.object({
    type: z.literal("LineString"),
    coordinates: z.array(z.tuple([z.number(), z.number()])),
});

export type RouteGeometry = z.infer<typeof RouteGeometrySchema>;

const TransportLinkModeApiSchema = z
    .enum(["LOCAL", "LINKED", "local", "linked"])
    .transform((value) => value.toUpperCase() as "LOCAL" | "LINKED");
export const DistanceModeApiSchema = z
    .enum([
        "MANUAL",
        "COMPUTED_FROM_COORDS",
        "COPIED_FROM_REFERENCE",
        "COMPUTED_FROM_ROUTE",
        "manual",
        "computed_from_coords",
        "copied_from_reference",
        "computed_from_route",
    ])
    .transform((value) => value.toUpperCase() as TransportLegDistanceMode);

export type TransportLinkMode = "LOCAL" | "LINKED";
export type TransportLegDistanceMode =
    | "MANUAL"
    | "COMPUTED_FROM_COORDS"
    | "COPIED_FROM_REFERENCE"
    | "COMPUTED_FROM_ROUTE";

export const TransportLegSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    organization: z.union([z.string(), z.object({ "@id": z.string() })]).optional(),
    linkMode: TransportLinkModeApiSchema,
    sourceReferenceLegId: z.number().nullable().optional(),
    name: z.string().nullable().optional(),
    transportModeId: z.number().nullable().optional(),
    originLabel: z.string().nullable().optional(),
    originCountryCode: z.string().nullable().optional(),
    originLatitude: z.number().nullable().optional(),
    originLongitude: z.number().nullable().optional(),
    destinationLabel: z.string().nullable().optional(),
    destinationCountryCode: z.string().nullable().optional(),
    destinationLatitude: z.number().nullable().optional(),
    destinationLongitude: z.number().nullable().optional(),
    distanceKm: z.number().nullable().optional(),
    distanceMode: DistanceModeApiSchema.nullable().optional(),
    routeGeometry: RouteGeometrySchema.nullable().optional(),
    isRefrigerated: z.boolean(),
});

export type TransportLeg = z.infer<typeof TransportLegSchema>;
export type TransportLegsState = RemoteCollectionState<TransportLeg>;

export type TransportLegWrite = {
    "@id"?: string;
    organization: IRI;
    linkMode: TransportLinkMode;
    sourceReferenceLegId?: number | null;
    name?: string | null;
    transportModeId?: number | null;
    originLabel?: string | null;
    originCountryCode?: string | null;
    originLatitude?: number | null;
    originLongitude?: number | null;
    destinationLabel?: string | null;
    destinationCountryCode?: string | null;
    destinationLatitude?: number | null;
    destinationLongitude?: number | null;
    distanceKm?: number | null;
    distanceMode?: TransportLegDistanceMode | null;
    routeGeometry?: RouteGeometry | null;
    isRefrigerated?: boolean;
};

export const transportLegsKey = "transportLegs";

export const transportLegKeys = {
    list: (organizationId?: number) => [transportLegsKey, "list", organizationId ?? null] as const,
    detail: (organizationId?: number, id?: number) =>
        [transportLegsKey, "detail", organizationId ?? null, id ?? null] as const,
};

export const useTransportLegs = (params?: { organizationId?: number; enabled?: boolean }) => {
    const organizationId = params?.organizationId;
    const enabled = (params?.enabled ?? true) && organizationId !== undefined;

    const query = useQuery({
        enabled,
        keepPreviousData: false,
        queryKey: transportLegKeys.list(organizationId),
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load transport legs while disabled or without an organization");
            }

            const members = await fetchAllHydraMembers("/api/transport-legs", {
                organization: organizationId,
            });
            return zodParseWithLog(members, TransportLegSchema.array(), "useTransportLegs");
        },
    });
    const transportLegsState: TransportLegsState = getRemoteCollectionState({
        data: query.data,
        enabled,
        error: query.error,
        isLoading: query.isLoading,
    });

    return {
        transportLegs: enabled ? (query.data ?? []) : undefined,
        transportLegsState,
        ...query,
        data: enabled ? query.data : undefined,
    };
};

export const useTransportLeg = (id?: number, options?: { enabled?: boolean; organizationId?: number }) => {
    const organizationId = options?.organizationId;
    const enabled = (options?.enabled ?? true) && id !== undefined && organizationId !== undefined;
    const query = useQuery({
        enabled,
        keepPreviousData: false,
        queryKey: transportLegKeys.detail(organizationId, id),
        queryFn: async () => {
            if (id === undefined || organizationId === undefined) {
                throw new Error("A transport leg id and organization id are required");
            }

            const request = await loggedInApiCall.get(`/api/transport-legs/${id}`);
            const transportLeg = zodParseWithLog(request.data, TransportLegSchema, "useTransportLeg");
            return assertTransportDetailScope(transportLeg, id, organizationId, "Transport leg");
        },
    });

    return {
        isNotFound: query.isError && isTransportDetailNotFoundError(query.error),
        transportLeg: query.data,
        ...query,
    };
};

export const useUpsertTransportLeg = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (leg: TransportLegWrite & { id?: number }) => {
            const payload = toTransportLegApiPayload(leg);
            const response =
                leg.id !== undefined ?
                    await loggedInApiCall.put("/api/transport-legs/" + leg.id, payload)
                :   await loggedInApiCall.post("/api/transport-legs", payload);

            try {
                return zodParseWithLog(response.data, TransportLegSchema, "useUpsertTransportLeg");
            } catch (error) {
                throw new TransportMutationContractError(error);
            } finally {
                void queryClient.invalidateQueries([transportLegsKey]);
            }
        },
    });
};

const toTransportLegApiPayload = (leg: TransportLegWrite & { id?: number }) => ({
    ...leg,
    linkMode: leg.linkMode.toLowerCase(),
    distanceMode: leg.distanceMode?.toLowerCase() ?? null,
});

export const useDeleteTransportLeg = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete("/api/transport-legs/" + id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([transportLegsKey]);
        },
    });
};
